// App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Toast from './components/Toast';
import AskNewaLauncher from './components/AskNewaLauncher';
import Login from './pages/Login';
import AskNewaWorkspace from './pages/AskNewaWorkspace';

import Overview from './pages/Overview';
import CRM from './pages/CRM';
import CrmOverview from './pages/CrmOverview';
import CrmContacts from './pages/CrmContacts';
import CrmContactProfile from './pages/CrmContactProfile';
import CrmCampaigns from './pages/CrmCampaigns';
import CrmPipeline from './pages/CrmPipeline';
import CrmAccounts from './pages/CrmAccounts';
import CrmTasks from './pages/CrmTasks';
import CrmIntegration from './pages/CrmIntegration';
import Projects from './pages/Projects';
import Inventory from './pages/Inventory';
import Finance from './pages/Finance';
import HR from './pages/HR';
import Reports from './pages/Reports';
import Marketplace from './pages/Marketplace';
import { useAuth } from './utils/hooks/useAuth';

import { Loader } from './utils/Loader';

// No Access Component
const NoAccess = () => {
  const { isOwner } = useAuth();
  
  return (
    <div
      style={{
        height: "70vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        textAlign: "center",
        padding: "20px"
      }}
    >
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
      <h2 style={{ marginBottom: '10px' }}>No IRP Module Access</h2>
      <p style={{ color: '#666', maxWidth: '400px', marginBottom: '20px' }}>
        You don't have permission to access any IRP modules.
        {isOwner ? (
          <span> As an ecosystem owner, you can enable modules from the IRP Access Control settings.</span>
        ) : (
          <span> Please contact your ecosystem owner to enable access for your role.</span>
        )}
      </p>
      {isOwner && (
        <button
          onClick={() => window.location.href = '/irp-access-control'}
          style={{
            background: '#1a56db',
            color: 'white',
            border: 'none',
            padding: '10px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Manage Permissions
        </button>
      )}
    </div>
  );
};

// Protected Route Component with Module Access Check
const ModuleProtectedRoute = ({ children, moduleKey, access, redirectTo }) => {
  if (!access) {
    return <Loader text="Checking permissions..." subText="Verifying your access rights" />;
  }

  let hasAccess = false;
  
  if (moduleKey === 'overview') {
    hasAccess = access?.permissions?.overview?.enabled === true;
  } else {
    hasAccess = access?.permissions?.[moduleKey]?.enabled === true;
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo || '/'}  />;
  }

  return children;
};

// Main App Content Component (Protected)
const AppContent = () => {
  const location = useLocation();
  const { access, loading, isOwner } = useAuth();

  const [toast, setToast] = React.useState('');
  function showToast(msg = '') {
    setToast(msg);
    setTimeout(() => setToast(''), 3200);
  }

  const moduleRoutes = [
    { key: "overview", path: "/" },
    { key: "iurm", path: "/crm/overview" },
    { key: "iproject", path: "/projects" },
    { key: "imim", path: "/inventory" },
    { key: "ifim", path: "/finance" },
    { key: "ihim", path: "/hr" },
    { key: "reportsAndAutonomy", path: "/reports" },
  ];

  const firstAccessibleRoute = React.useMemo(() => {
    if (!access?.permissions) return null;

    for (const item of moduleRoutes) {
      if (item.key === "overview") {
        if (access.permissions.overview?.enabled) {
          return item.path;
        }
      } else {
        if (access.permissions[item.key]?.enabled) {
          return item.path;
        }
      }
    }
    return null;
  }, [access]);

  const hasAnyAccess = React.useMemo(() => {
    if (!access?.permissions) return false;
    
    if (access.permissions.overview?.enabled) return true;
    
    const moduleKeys = ['iurm', 'iproject', 'imim', 'ifim', 'ihim', 'reportsAndAutonomy'];
    return moduleKeys.some(key => access.permissions[key]?.enabled === true);
  }, [access]);

  if (loading) {
    return <Loader text="Loading your workspace..." subText="Please wait while we prepare your IRP modules" />;
  }

  return (
    <div className="screen-wrapper">
      <div className="screen relative">
        <Sidebar />
        <div className="main">
          <Topbar />
          <div className="content-row">
            <div className="content-main">
              <Routes>
                <Route 
                  path="/" 
                  element={
                    !hasAnyAccess ? (
                      <NoAccess />
                    ) : access?.permissions?.overview?.enabled ? (
                      <Overview showToast={showToast} />
                    ) : (
                      <Navigate to={firstAccessibleRoute} replace />
                    )
                  } 
                />

                <Route 
                  path="/crm" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <Navigate to="/crm/overview" replace />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/overview" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <CrmOverview />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/leads" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <CRM />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/contacts" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <CrmContacts />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/contacts/:id" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <CrmContactProfile />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/campaigns" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <CrmCampaigns />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/pipeline" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <CrmPipeline />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/accounts" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <CrmAccounts />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/tasks" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <CrmTasks />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route 
                  path="/crm/integration" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iurm" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <CrmIntegration />
                    </ModuleProtectedRoute>
                  } 
                />

                <Route 
                  path="/projects" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="iproject" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <Projects />
                    </ModuleProtectedRoute>
                  } 
                />

                <Route 
                  path="/inventory" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="imim" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <Inventory />
                    </ModuleProtectedRoute>
                  } 
                />

                <Route 
                  path="/finance" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="ifim" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <Finance />
                    </ModuleProtectedRoute>
                  } 
                />

                <Route 
                  path="/hr" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="ihim" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <HR />
                    </ModuleProtectedRoute>
                  } 
                />

                <Route 
                  path="/reports" 
                  element={
                    <ModuleProtectedRoute 
                      moduleKey="reportsAndAutonomy" 
                      access={access} 
                      redirectTo={firstAccessibleRoute || '/'}
                    >
                      <Reports />
                    </ModuleProtectedRoute>
                  } 
                />

                <Route path="/marketplace" element={<Marketplace showToast={showToast} />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </div>
        </div>
        <AskNewaLauncher />
        {/* <div className="ask-newa-pill">
          <div className="ask-newa-orbit"></div>
          Ask NEWA
        </div> */}
      </div>
      <Toast message={toast} />
    </div>
  );
};

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const location = useLocation();

useEffect(() => {
  const checkAuth = () => {
    const token = sessionStorage.getItem('kt-auth-react-v');
    setIsAuthenticated(!!token);
  };

  checkAuth();

  const handleStorageChange = (e) => {
    if (e.key === 'kt-auth-react-v') {
      setIsAuthenticated(!!e.newValue);
    }
  };

  // Cross-tab changes
  window.addEventListener('storage', handleStorageChange);
  // Same-tab changes (login/logout happening in this window itself,
  // since 'storage' events never fire in the tab that made the change)
  window.addEventListener('kt-auth-changed', checkAuth);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener('kt-auth-changed', checkAuth);
  };
}, []);

  // Show loader while checking authentication
  if (isAuthenticated === null) {
    return <Loader text="Checking authentication..." subText="Please wait" />;
  }

  // If user is authenticated, render app content
  if (isAuthenticated) {
    return (
      <div className="app-shell">
        <Routes>
          <Route path="/login" element={<Navigate to="/"  />} />
          {/* Full-screen AskNEWA workspace — no shell, no sidebar */}
          <Route path="/asknewa" element={<AskNewaWorkspace />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </div>
    );
  }

  // If user is not authenticated, show login
  return (
    <div className="app-shell">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/*" element={<Navigate to="/login"  />} />
      </Routes>
    </div>
  );
}